/* The script tag lives inside <product-info> in main-product.liquid, so the
 * quick-add modal re-executes this file when it injects a fetched product page
 * (HTMLUpdateUtility re-runs embedded scripts) — guard against redeclaration. */
if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        const sectionId = this.dataset.sectionId;

        this._observer = new IntersectionObserver(
          (entries) => {
            const isVisible = entries[0].isIntersecting;
            this.classList.toggle('sticky-atc--visible', !isVisible);
          },
          { threshold: 0, rootMargin: '0px' }
        );

        this._mainSubmit = document.getElementById(`ProductSubmitButton-${sectionId}`);

        const target = this._mainSubmit?.closest('.product-form__buttons');
        if (target) this._observer.observe(target);

        // When no variant is selected the main ATC is guarded (aria-disabled). The sticky
        // buttons submit the same form, so the "select an option" message is rendered up in
        // the main form — scroll it into view so the shopper can see it.
        this.addEventListener('click', (event) => {
          if (!event.target.closest('.sticky-atc__button, .sticky-atc__button-mobile')) return;
          if (this._mainSubmit?.getAttribute('aria-disabled') === 'true') {
            this._mainSubmit.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });

        // The sticky buttons are a proxy for product-form__submit, so mirror its
        // state instead of deriving one: unavailable / sold out / the "choose a
        // size" guard / add to cart all resolve in one place, and the sticky
        // button becomes addable the moment a variant is selected.
        if (this._mainSubmit) {
          this._syncFromMainSubmit();

          this._stateObserver = new MutationObserver(() => this._syncFromMainSubmit());
          this._stateObserver.observe(this._mainSubmit, {
            attributes: true,
            attributeFilter: ['disabled', 'aria-disabled'],
            childList: true,
            subtree: true,
            characterData: true,
          });
        }

        this._unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId !== sectionId) return;
          this._updatePrice(event.data.html, sectionId);
        });
      }

      disconnectedCallback() {
        this._observer?.disconnect();
        this._stateObserver?.disconnect();
        this._unsubscribe?.();
      }

      _syncFromMainSubmit() {
        const source = this._mainSubmit;
        if (!source) return;

        const isDisabled = source.hasAttribute('disabled');
        const isGuarded = source.getAttribute('aria-disabled') === 'true';
        const label = source.querySelector('span')?.textContent.trim();

        [
          [this.querySelector('.sticky-atc__button'), '.sticky-atc__button-text'],
          [this.querySelector('.sticky-atc__button-mobile'), '.sticky-atc__button-mobile-text'],
        ].forEach(([button, textSelector]) => {
          if (!button) return;

          button.disabled = isDisabled;
          if (isGuarded) {
            button.setAttribute('aria-disabled', 'true');
          } else {
            button.removeAttribute('aria-disabled');
          }

          const textEl = button.querySelector(textSelector);
          if (textEl && label) textEl.textContent = label;
        });
      }

      _updatePrice(html, sectionId) {
        const srcPrice = html.getElementById(`price-${sectionId}`);

        const destPrice = this.querySelector(`#sticky-price-${sectionId}`);
        if (srcPrice && destPrice) destPrice.innerHTML = srcPrice.innerHTML;

        const destPriceMobile = this.querySelector(`#sticky-price-mobile-${sectionId}`);
        if (srcPrice && destPriceMobile) {
          const clone = srcPrice.cloneNode(true);
          clone.querySelector('.price__badges')?.remove();
          destPriceMobile.innerHTML = clone.innerHTML;
        }
      }
    }
  );
}
